import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ComunicacaoPje, fetchTodasComunicacoesPorOab, onlyDigits } from '@/services/comunicaPjeService';

const OAB_KEY = 'intimacoes_oab';
const DEFAULT_OAB = { numero: '67173', uf: 'PE' };

const loadOab = (): { numero: string; uf: string } => {
  try {
    const raw = localStorage.getItem(OAB_KEY);
    return raw ? JSON.parse(raw) : DEFAULT_OAB;
  } catch {
    return DEFAULT_OAB;
  }
};

const normalizeName = (value: string) =>
  value.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

const capitalize = (value: string) => {
  const lower = value.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

interface FoundProcess {
  numero: string; // apenas dígitos
  numeroMascara: string;
  tribunal: string;
  orgao: string;
  classe: string;
  autores: string[];
  reus: string[];
  ultimaComunicacao: string;
  totalComunicacoes: number;
}

// Agrupa as comunicações por processo, mantendo os dados da mais recente
const groupByProcess = (items: ComunicacaoPje[]): FoundProcess[] => {
  const map = new Map<string, FoundProcess>();
  for (const item of items) {
    const existing = map.get(item.numero_processo);
    if (existing) {
      existing.totalComunicacoes++;
      item.destinatarios.forEach((d) => {
        const list = d.polo === 'P' ? existing.reus : existing.autores;
        if (!list.includes(d.nome)) list.push(d.nome);
      });
      continue;
    }
    map.set(item.numero_processo, {
      numero: item.numero_processo,
      numeroMascara: item.numeroprocessocommascara,
      tribunal: item.siglaTribunal,
      orgao: item.nomeOrgao,
      classe: item.nomeClasse ? capitalize(item.nomeClasse) : 'Processo Judicial',
      autores: item.destinatarios.filter((d) => d.polo !== 'P').map((d) => d.nome),
      reus: item.destinatarios.filter((d) => d.polo === 'P').map((d) => d.nome),
      ultimaComunicacao: item.data_disponibilizacao,
      totalComunicacoes: 1,
    });
  }
  return [...map.values()];
};

interface ImportOabProcessesModalProps {
  onImported: () => void;
  /** Botão só com ícone (barra de ferramentas da página Processos) */
  iconOnly?: boolean;
}

export const ImportOabProcessesModal = ({ onImported, iconOnly = false }: ImportOabProcessesModalProps) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newProcesses, setNewProcesses] = useState<FoundProcess[]>([]);
  const [alreadyCount, setAlreadyCount] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const oab = loadOab();

  const search = async () => {
    setLoading(true);
    setError(null);
    try {
      const [items, casesResult] = await Promise.all([
        fetchTodasComunicacoesPorOab(oab.numero, oab.uf),
        supabase.from('cases').select('case_number, case_number_cnj'),
      ]);
      if (casesResult.error) throw casesResult.error;

      const existing = new Set<string>();
      (casesResult.data || []).forEach((c: any) => {
        [c.case_number_cnj, c.case_number].forEach((n) => {
          const digits = onlyDigits(n);
          if (digits) existing.add(digits);
        });
      });

      const all = groupByProcess(items);
      const fresh = all.filter((p) => !existing.has(p.numero));
      setNewProcesses(fresh);
      setAlreadyCount(all.length - fresh.length);
      setSelected(new Set(fresh.map((p) => p.numero)));
    } catch (e: any) {
      setError(e?.message || 'Erro ao buscar processos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    if (value) search();
  };

  const toggle = (numero: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(numero) ? next.delete(numero) : next.add(numero);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(selected.size === newProcesses.length ? new Set() : new Set(newProcesses.map((p) => p.numero)));
  };

  const importSelected = async () => {
    setImporting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Vincula automaticamente ao cliente quando o nome do autor bate com um cliente cadastrado
      const { data: clients } = await supabase.from('clients').select('id, name');
      const clientsByName = new Map((clients || []).map((c) => [normalizeName(c.name), c.id]));

      const rows = newProcesses
        .filter((p) => selected.has(p.numero))
        .map((p) => {
          const clientId = p.autores.map((a) => clientsByName.get(normalizeName(a))).find(Boolean) || null;
          const parte = p.autores[0] || p.reus[0];
          return {
            user_id: user.id,
            title: parte ? `${p.classe} - ${parte}` : p.classe,
            case_number_cnj: p.numeroMascara,
            case_type: p.classe,
            court: p.tribunal,
            vara: p.orgao,
            opposing_party: p.reus.join(', ') || null,
            client_id: clientId,
            status: 'Em andamento',
            priority: 'Média',
            description:
              `Processo importado automaticamente do Diário de Justiça Eletrônico Nacional (OAB ${oab.numero}/${oab.uf}).` +
              (p.autores.length ? `\nPolo ativo: ${p.autores.join(', ')}` : '') +
              (p.reus.length ? `\nPolo passivo: ${p.reus.join(', ')}` : ''),
          };
        });

      const { error } = await supabase.from('cases').insert(rows as any);
      if (error) throw error;

      const linked = rows.filter((r) => r.client_id).length;
      toast({
        title: `${rows.length} processo${rows.length === 1 ? '' : 's'} importado${rows.length === 1 ? '' : 's'}`,
        description: linked
          ? `${linked} vinculado${linked === 1 ? '' : 's'} automaticamente a clientes cadastrados.`
          : 'Revise os processos para vincular os clientes.',
      });
      setOpen(false);
      onImported();
    } catch (e: any) {
      toast({ title: 'Erro ao importar', description: e?.message || 'Tente novamente.', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      {iconOnly ? (
        <button type="button" onClick={() => handleOpenChange(true)} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-[#faf7f2] hover:text-[#7a6440]" title="Importar processos da OAB" aria-label="Importar processos da OAB">
          <Download className="h-4 w-4" />
        </button>
      ) : (
        <Button variant="outline" onClick={() => handleOpenChange(true)}>
          <Download className="w-4 h-4 mr-2" />
          Importar da OAB
        </Button>
      )}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Importar processos da OAB {oab.numero}/{oab.uf}</DialogTitle>
            <DialogDescription>
              Processos encontrados nas intimações do Diário de Justiça Eletrônico Nacional (desde 2023).
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Buscando processos no CNJ...
            </div>
          )}

          {error && (
            <div className="text-sm text-destructive flex items-center justify-between gap-2 py-2">
              <span>{error}</span>
              <Button size="sm" variant="outline" onClick={search}>Tentar novamente</Button>
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span>
                  <strong>{newProcesses.length}</strong> novo{newProcesses.length === 1 ? '' : 's'}
                  {alreadyCount > 0 && ` · ${alreadyCount} já cadastrado${alreadyCount === 1 ? '' : 's'}`}
                </span>
                {newProcesses.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={toggleAll}>
                    {selected.size === newProcesses.length ? 'Desmarcar todos' : 'Marcar todos'}
                  </Button>
                )}
              </div>
              <div className="max-h-[50vh] overflow-y-auto space-y-2">
                {newProcesses.length === 0 && (
                  <p className="text-sm text-muted-foreground py-4">Todos os processos da OAB já estão cadastrados.</p>
                )}
                {newProcesses.map((p) => (
                  <label
                    key={p.numero}
                    className="flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted transition-colors"
                  >
                    <Checkbox
                      checked={selected.has(p.numero)}
                      onCheckedChange={() => toggle(p.numero)}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-sm">{p.numeroMascara}</span>
                        <Badge variant="outline">{p.tribunal}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{p.classe} · {p.orgao}</p>
                      {(p.autores.length > 0 || p.reus.length > 0) && (
                        <p className="text-xs truncate">
                          {[...p.autores, ...p.reus.map((r) => `${r} (réu)`)].join(', ')}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={importSelected} disabled={loading || importing || selected.size === 0}>
              {importing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Importar {selected.size} processo{selected.size === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
